import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, ThemeProvider, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { usePostHog } from 'posthog-js/react';
import { useLazyQuery } from '@apollo/client';

import {
  ContentContainer,
  HeaderBox,
  Title,
  OtpInputContainer,
  OtpInput,
  ErrorText,
  ResendButtonBox,
  getResendButtonStyles,
} from './SmsVerify.styles';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Product } from '@/utils/productHelpers';

import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkConfirm, requestAuthCode } from '@/utils/sparkActivationApi';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { ME_QUERY } from '@/apollo/queries';
import { logError } from '@/utils/logError';

interface SmsVerifyLocationState {
  phoneNumber?: string;
  marketingConsent?: boolean;
  limbixUserId?: string;
  accessCode?: string;
  fromLogin?: boolean;
  enrollment?: string;
  orgSlug?: string;
}

const CODE_LENGTH = 6;
const INPUT_POSITIONS = [0, 1, 2, 3, 4, 5] as const;

const SmsVerifyInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const { t } = useTranslation();
  const theme = useTheme();
  const posthog = usePostHog();
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const { state: accountData } = useLocation<SmsVerifyLocationState>();
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasSubmittedRef = useRef(false);
  const fromLogin = accountData?.fromLogin ?? false;
  const isComplete = code.every((digit) => digit !== '');
  const [fetchMe] = useLazyQuery(ME_QUERY, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    posthog?.capture('SparkSmsVerifyScreenViewed');
    if (fromLogin) {
      posthog?.capture('LoginOTPLanded');
    } else {
      posthog?.capture('SignUpOTPLanded', {
        phone_number: accountData?.phoneNumber,
        input: accountData?.phoneNumber,
      });
    }
  }, [posthog, fromLogin, accountData?.phoneNumber]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) { return undefined; }
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Verifies the OTP token against Supabase once the user has filled all 6 digits.
  const verifyOtpCode = useCallback(async (token: string) => {
    if (!accountData?.phoneNumber) { return; }

    setIsVerifying(true);
    setError('');

    const { data: verifyData, error: verifyError } = await getSupabaseClient().auth.verifyOtp({
      phone: accountData.phoneNumber,
      token,
      type: 'sms',
    });

    if (verifyError) {
      if (fromLogin) {
        posthog?.capture('LoginOTPSubmittedFailed', {
          phone_number: accountData?.phoneNumber,
          input: accountData?.phoneNumber,
          otp: token,
        });
      } else {
        posthog?.capture('SignUpOTPFailed', {
          phone_number: accountData?.phoneNumber,
          input: accountData?.phoneNumber,
          otp: token,
        });
      }
      // Both invalid and expired OTP errors surface the same generic message per AC.
      setError(t('coverage.pre_onboarding.sms_verify.error_invalid'));
      setCode(Array(CODE_LENGTH).fill(''));
      hasSubmittedRef.current = false;
      setIsVerifying(false);
      return;
    }

    const supabaseUserId = verifyData?.user?.id;
    posthog?.capture('SparkSmsVerifySuccess', { supabase_user_id: supabaseUserId });
    if (fromLogin) {
      posthog?.capture('LoginOTPSubmittedSucceeded', {
        phone_number: accountData?.phoneNumber,
        input: accountData?.phoneNumber,
        otp: token,
      });
    } else {
      posthog?.capture('SignUpOTPSucceeded', {
        phone_number: accountData?.phoneNumber,
        input: accountData?.phoneNumber,
        otp: token,
      });
    }

    // Activation flow: confirm with backend then collect user info
    if (!fromLogin) {
      try {
        await activateSparkConfirm({
          supabaseUserId: supabaseUserId ?? '',
          accessToken: verifyData?.session?.access_token ?? '',
          phDistinctId: posthog?.get_distinct_id(),
          lob: product === 'sleepiorx' ? 'rx' : undefined,
          product: product === 'sleepiorx' ? 'sleepio' : undefined,
        });
      } catch (err) {
        logError('[SmsVerify]', 'activation failed', err);
        setError(t('coverage.errors.generic_error'));
        setCode(Array(CODE_LENGTH).fill(''));
        hasSubmittedRef.current = false;
        setIsVerifying(false);
        return;
      }
      // Employer flow: go to coverage check. Access code flow: go to demographics.
      if (accountData?.orgSlug) {
        history.push(`/activate/${product}/coverage-check/${accountData.orgSlug}`, {
          patientUid: accountData?.limbixUserId,
          phoneNumber: accountData?.phoneNumber,
          accessToken: verifyData?.session?.access_token,
        });
      } else {
        history.push(`/activate/${product}/user-info/confirmation`, {
          accessCode: accountData?.accessCode,
          phoneNumber: accountData?.phoneNumber,
          patientUid: accountData?.limbixUserId,
        });
      }
      return;
    }

    // Login flow: skip activation, go straight to session handoff
    const {
      data: { session: refreshedSession },
      error: refreshError,
    } = await getSupabaseClient().auth.refreshSession();
    if (refreshError || !refreshedSession?.access_token || !refreshedSession?.refresh_token) {
      logError('[SmsVerify]', 'refreshSession failed', refreshError ?? new Error('no session'));
      setError(t('coverage.errors.generic_error'));
      setCode(Array(CODE_LENGTH).fill(''));
      hasSubmittedRef.current = false;
      setIsVerifying(false);
      return;
    }

    const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);

    // Browser context: no in-app deep link available — route to download page.
    if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
      history.push(`/activate/${product}/download-app`);
      return;
    }

    let exchangeToken: string;
    try {
      ({ exchangeToken } = await requestAuthCode(
        refreshedSession.access_token,
        refreshedSession.refresh_token,
      ));
    } catch (err) {
      logError('[SmsVerify]', 'requestAuthCode failed', err);
      setError(t('coverage.errors.generic_error'));
      setCode(Array(CODE_LENGTH).fill(''));
      hasSubmittedRef.current = false;
      setIsVerifying(false);
      return;
    }

    const { data: meData } = await fetchMe({
      context: { headers: { Authorization: `Bearer ${refreshedSession.access_token}` } },
    });
    const limbixUserUid: string | undefined = meData?.me?.uid ?? undefined;

    const deepLinkUrl = new URL(redirectTo);
    deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
    if (limbixUserUid) {
      deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
    }
    const deepLink = deepLinkUrl.toString();

    window.location.href = deepLink;
  }, [accountData, product, history, posthog, t]);

  useEffect(() => {
    if (isComplete && !hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      const otp = code.join('');
      posthog?.capture('SparkSmsVerifyCodeEntered');
      if (fromLogin) {
        posthog?.capture('LoginOTPSubmitted', {
          phone_number: accountData?.phoneNumber,
          input: accountData?.phoneNumber,
          otp,
        });
      } else {
        posthog?.capture('SignUpOTPSubmitted', {
          phone_number: accountData?.phoneNumber,
          input: accountData?.phoneNumber,
          otp,
        });
      }
      verifyOtpCode(otp);
    }
  }, [isComplete, code, posthog, accountData, fromLogin, verifyOtpCode]);

  const focusInput = (index: number) => inputRefs.current[index]?.focus();

  const resetState = () => {
    setError('');
    setResendSuccess(false);
    hasSubmittedRef.current = false;
  };

  const updateCode = (index: number, digit: string) => {
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    resetState();
  };

  const fillCode = (digits: string, startIndex: number) => {
    const newCode = [...code];
    digits.split('').forEach((digit, i) => {
      if (startIndex + i < CODE_LENGTH) {
        newCode[startIndex + i] = digit;
      }
    });
    setCode(newCode);
    resetState();
    const nextEmpty = newCode.findIndex((d) => !d);
    focusInput(nextEmpty === -1 ? CODE_LENGTH - 1 : nextEmpty);
  };

  const handleChange = (index: number, value: string) => {
    const digits = value.replace(/\D/g, '');
    // iOS SMS autofill may deliver the full OTP through onChange
    if (digits.length > 1) {
      fillCode(digits.slice(0, CODE_LENGTH), 0);
      return;
    }
    const digit = digits.slice(-1);
    updateCode(index, digit);
    if (digit && index < CODE_LENGTH - 1) { focusInput(index + 1); }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    fillCode(digits, index);
  };

  // Resends the OTP by calling signInWithOtp again with the same phone number.
  const handleResendCode = async () => {
    if (isResending) { return; }

    posthog?.capture('SparkSmsVerifyResendClicked');
    if (fromLogin) {
      posthog?.capture('LoginOTPRequested', {
        phone_number: accountData?.phoneNumber,
        input: accountData?.phoneNumber,
        otp: code.join(''),
      });
    }
    setError('');
    setResendSuccess(false);

    if (!accountData?.phoneNumber) { return; }

    setIsResending(true);
    try {
      const { error: resendError } = await getSupabaseClient().auth.signInWithOtp({
        phone: accountData.phoneNumber,
      });

      if (resendError) {
        setError(t('coverage.pre_onboarding.sms_verify.resend_error'));
        return;
      }

      if (fromLogin) {
        posthog?.capture('LoginOTPSent', {
          phone_number: accountData?.phoneNumber,
          input: accountData?.phoneNumber,
        });
      }
      setResendSuccess(true);
      setCode(Array(CODE_LENGTH).fill(''));
      hasSubmittedRef.current = false;
      if (product === 'sleepiorx') { setResendCooldown(60); }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <ContentContainer>
      <Header />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.sms_verify.title')}
          </Title>
          <Typography color="textPrimary">
            {t(fromLogin ? 'coverage.spark.login.otp_sub_title' : 'coverage.pre_onboarding.sms_verify.sub_title')}
          </Typography>
        </HeaderBox>

        <OtpInputContainer data-testid="sms-otp-input">
          {INPUT_POSITIONS.map((i) => (
            <OtpInput
              key={i}
              type="text"
              inputMode="numeric"
              maxLength={1}
              autoComplete="one-time-code"
              value={code[i]}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
              ref={(el) => { inputRefs.current[i] = el; }}
              data-testid={`otp-input-${i}`}
              className={error ? 'error' : ''}
              disabled={isVerifying}
              aria-label={t('coverage.pre_onboarding.sms_verify.input_aria_label', { position: i + 1 })}
            />
          ))}
        </OtpInputContainer>

        {error && <ErrorText data-testid="error-message">{error}</ErrorText>}

        <ResendButtonBox>
          <Button
            variant="contained"
            data-testid="resend-code-button"
            type="button"
            fullWidth
            style={getResendButtonStyles(theme)}
            onClick={handleResendCode}
            disabled={isVerifying || isResending || resendCooldown > 0}
          >
            {resendCooldown > 0
              ? t('coverage.pre_onboarding.sms_verify.resend_code_wait', { seconds: resendCooldown })
              : t('coverage.pre_onboarding.sms_verify.resend_code')}
          </Button>

          {resendSuccess && (
            <Typography
              color="textPrimary"
              data-testid="resend-success-message"
              style={{ marginTop: 8, fontSize: 14, textAlign: 'center' }}
            >
              {t('coverage.pre_onboarding.sms_verify.resend_success')}
            </Typography>
          )}
        </ResendButtonBox>

        <ConsentWidget />
      </Container>
      {product !== 'sleepiorx' && <Footer />}
    </ContentContainer>
  );
};

const SmsVerify: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <SmsVerifyInner />
  </ThemeProvider>
);

export default SmsVerify;
